import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import Button from "../Button";
import Dropdown from "../Dropdown";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FilterItem } from "./FilterItem";
import { RichDatePickerPanel } from "../DatePicker/RichDatePickerPanel";

export const Filters = (props) => {
  const [filters, setFilters] = useState([]);
  const [autoOpenFilterId, setAutoOpenFilterId] = useState(null);
  const hasAppliedInitialFiltersRef = useRef(false);

  // Allow pages (ex: Dashboard -> "View All") to prefill filters so the index list
  // matches what the user just saw.
  //
  // We only apply this once when the local filters are still empty, so we don't
  // stomp on user changes.
  useEffect(() => {
    if (!Array.isArray(props.initialFilters) || props.initialFilters.length === 0)
      return;
    if (hasAppliedInitialFiltersRef.current) return;
    if (filters.length > 0) return;

    hasAppliedInitialFiltersRef.current = true;
    setFilters(props.initialFilters);
    // Immediately apply them so the parent query matches the UI.
    // (This triggers `onFiltersChange` on the page through the existing plumbing.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    props.onFiltersChange && props.onFiltersChange(props.initialFilters);
  }, [props.initialFilters, filters.length]);

  // Check if a filter has a meaningful value
  function hasFilterValue(filter) {
    if (!filter) return false;
    const value = filter.value;
    if (value === undefined || value === null || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    
    // For date filters, check if value is not the default "allTime"
    if (filter.type === "date") {
      // Support both legacy and current shapes:
      // - legacy: { id: "allTime", value: null }
      // - current: { start, end }
      if (value?.id === "allTime") return false;
      if ("value" in (value || {}) && value?.value === null) return false;
      if ("start" in (value || {}) || "end" in (value || {})) {
        return !!value?.start || !!value?.end;
      }
      return true;
    }
    
    // For other filters, check if value is not null, undefined, or empty string
    return value !== filter.defaultValue?.value;
  }

  // Filter out filters without values before updating parent
  function getActiveFilters(arr) {
    return arr.filter(hasFilterValue);
  }

  function updateFilters(arr) {
    // Only pass filters with actual values to parent to prevent unnecessary reloads
    const activeFilters = getActiveFilters(arr);
    props.onFiltersChange && props.onFiltersChange(activeFilters);
  }

  function handleFilterChange(arr) {
    setFilters(arr);
    updateFilters(arr);
  }

  function addFilter(e) {
    // Add filter to local state but don't trigger parent update until value is set
    setFilters((prev) => [...prev, e]);
    // Immediately open the options for the filter that was just selected
    setAutoOpenFilterId(e?.id || null);
    // Don't call updateFilters here - wait until user selects a value
  }

  function removeFilter(e) {
    const newFilters = filters.filter((i) => i.id !== e.id);
    setFilters(newFilters);
    if (autoOpenFilterId === e.id) setAutoOpenFilterId(null);
    // Always update parent when removing a filter to trigger reload
    // getActiveFilters will handle filtering out any empty values
    const activeFilters = getActiveFilters(newFilters);
    props.onFiltersChange && props.onFiltersChange(activeFilters);
  }

  const addFilterButton = (
    <Button id="1" type="light" size="xs">
      + Add Filter
    </Button>
  );

  const options = useMemo(() => props.filters, [props.filters]);

  function handleChange(e) {
    const filterData = filters.map((i) => {
      if (e.id === i.id) {
        return e;
      }
      return i;
    });
    handleFilterChange(filterData);
    // Once the user makes a selection for the auto-opened filter, stop forcing it open.
    if (autoOpenFilterId === e.id) {
      setAutoOpenFilterId(null);
    }
  }

  return (
    <div className="mt-2 flex items-center">
      <div className="mr-2">
        <Dropdown
          closeOnClick
          align="left"
          data={options}
          itemClick={addFilter}
          trigger={addFilterButton}
        />
      </div>
      <div className="flex items-center w-full space-x-2">
        {filters.map((item, idx) => {
          const defaultItem = props.filters.find((i) => i.id === item.id);
          const itemData = {
            id: item.id,
            type: item.type,
            label: item.label,
            content: item.content,
            defaultText: defaultItem.defaultText,
          };

          function dropdown(close) {
            const Component = item.content;
            return item.content ? (
              <Component
                close={close}
                value={item}
                onChange={(e) => {
                  handleChange({
                    displayText: e.title,
                    value: e.value,
                    ...itemData,
                  });
                }}
              />
            ) : (
              <RichDatePickerPanel
                close={close}
                value={item}
                onChange={(e) =>
                  handleChange({
                    displayText: e.title,
                    value: e.value,
                    ...itemData,
                    type: e.type,
                  })
                }
              />
            );
          }

          // const dropdown = item.content ? (
          //   (() => {
          //     const Component = item.content;
          //     return (
          //       <Component
          //         value={itemData}
          //         onChange={(e) => {
          //           handleChange({
          //             displayText: e.title,
          //             value: e.value,
          //             ...itemData,
          //           });
          //         }}
          //       />
          //     );
          //   })()
          // ) : (
          //   <RichDatePickerPanel
          //     value={item}
          //     onChange={(e) =>
          //       handleChange({
          //         displayText: e.title,
          //         value: e.value,
          //         ...itemData,
          //         type: e.type,
          //       })
          //     }
          //   />
          // );

          return (
            <FilterItem
              key={idx}
              dropdown={dropdown}
              title={item.label}
              value={item.value}
              displayText={item.displayText || item.defaultText}
              remove={() => removeFilter(item)}
              defaultOpen={autoOpenFilterId === item.id}
            />
          );
        })}
      </div>
    </div>
  );
};
